import { NonEmptyArray } from 'fp-ts/NonEmptyArray';
import { useState } from 'react';
import { Language } from '@mews-commander/data-access';
import { Option } from '@mews-commander/types';
import { createOption, Select } from '@mews-commander/ui';
import { lt } from '@mews/framework';
import { Divider, Size, SpacingToken, Stack } from '@optimus-web/core';
import { FormLanguageContextProvider } from './FormLanguageContext';

interface Props {
    fill?: boolean;
    spacing?: SpacingToken;
    languageSelectSize?: Size;
    initialLanguageCode?: string;
    defaultLanguageCode: string;
    languages: Array<Language>;
    enableAllOption?: boolean;
}

const ALL_LANGUAGES_CODE = 'ALL';

export const FormLanguageProvider = ({
    children,
    fill = true,
    spacing = '400',
    languageSelectSize = 'medium',
    initialLanguageCode,
    defaultLanguageCode,
    languages,
    enableAllOption,
}: React.PropsWithChildren<Props>) => {
    const defaultLanguage = languages.find(
        (language) => language.code === defaultLanguageCode
    ) as NonNullable<Language>;
    const initialLanguage = initialLanguageCode
        ? (languages.find((language) => language.code === initialLanguageCode) as NonNullable<Language>)
        : undefined;

    const [selectedLanguage, setLanguage] = useState(initialLanguage ?? defaultLanguage);

    const languageOptions = languages
        .filter((language): language is Language & { code: NonNullable<Language['code']> } => !!language.code)
        .map(
            ({ code, englishName }) =>
                createOption({ value: code, label: englishName }) as Option & { value: NonNullable<Option['value']> }
        );

    const options = enableAllOption
        ? [createOption({ value: ALL_LANGUAGES_CODE, label: lt('All') }), ...languageOptions]
        : languageOptions;

    let selectedLanguages: NonEmptyArray<Language>;

    if (selectedLanguage.code === ALL_LANGUAGES_CODE) {
        selectedLanguages = languages as NonEmptyArray<Language>;
    } else if (selectedLanguage.code === defaultLanguage.code) {
        selectedLanguages = [defaultLanguage];
    } else {
        selectedLanguages = [defaultLanguage, selectedLanguage];
    }

    return (
        <Stack spacing={spacing} fill={fill} vertical>
            <Select
                name="localization"
                label={lt('AddTranslation')}
                bottomCaption={lt('AddTranslationHint')}
                options={options}
                selected={selectedLanguage.code}
                onChange={(value) => {
                    const language = languages.find(({ code }) => code === value);
                    if (language) {
                        setLanguage(language);
                    }
                }}
                size={languageSelectSize}
            />
            <FormLanguageContextProvider value={selectedLanguages}>
                <Divider />
                {children}
            </FormLanguageContextProvider>
        </Stack>
    );
};
